import { useState, useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { my_routes } from "../../router/all_routes";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getAllUsers } from "../../../api/users";
import {
  getDecisionParameter,
  updateDecisionParameter,
} from "../../../api/diplome";

const DiplomeSettings = () => {
  const [allUsers, setAllUsers] = useState<any[]>([]);

  //Initialisation des state utilisés
  const createSchema = z.object({
    validatorDecision1: z.string().nullable(),
    validatorDecision2: z.string().nullable(),
    signatoryDiploma1: z.string().nullable(),
    signatoryDiploma2: z.string().nullable(),
    signatoryDiploma3: z.string().nullable(),
    postSignatoryDiploma1: z.string().nullable(),
    postSignatoryDiploma2: z.string().nullable(),
    postSignatoryDiploma3: z.string().nullable(),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const finalData = {
        ...data,
      };
      // console.log(finalData)
      //Envoi des datas pour la MÀJ vers le backend
      await updateDecisionParameter(finalData);

      toast.success("Paramètres de Diplôme/Décision mis à jour !"); //Toast de succès
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  //On recupère la liste des Years en appelant sa fonction
  const fetchUserProfil = async () => {
    try {
      const response = await getAllUsers();
      // console.log(response)
      setAllUsers(response);
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchDecisionParameter = async () => {
    try {
      const response = await getDecisionParameter();
      if (!response) {
        // Si aucune année trouvé
        throw new Error("Paramètres introuvable");
      }
      // console.log("Paramètres", response.data)
      reset({
        // Si année trouvé on rempli les champs du formulaire
        validatorDecision1: response.data.decisionParameter.validatorDecision1,
        validatorDecision2: response.data.decisionParameter.validatorDecision2,
        signatoryDiploma1: response.data.decisionParameter.signatoryDiploma1,
        signatoryDiploma2: response.data.decisionParameter.signatoryDiploma2,
        signatoryDiploma3: response.data.decisionParameter.signatoryDiploma3,
        postSignatoryDiploma1:
          response.data.decisionParameter.postSignatoryDiploma1,
        postSignatoryDiploma2:
          response.data.decisionParameter.postSignatoryDiploma2,
        postSignatoryDiploma3:
          response.data.decisionParameter.postSignatoryDiploma3,
      });
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchUserProfil();

    fetchDecisionParameter();
  }, []);

  return (
    <>
      <form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex align-items-center justify-content-between flex-wrap border-bottom pt-3 mb-3">
          <div className="mb-3">
            <h5 className="mb-1">Paramètres Diplôme/Décision</h5>
            <p>
              Personnalisez le parcours et les paramètres de votre application
            </p>
          </div>
          <div className="mb-3">
            <button className="btn btn-primary" type="submit">
              Enregistrer
            </button>
          </div>
        </div>
        <div className="d-md-flex d-block">
          <div className="flex-fill">
            <div className="card">
              <div className="card-header">
                <h5>Validateurs des Diplôme/Décision</h5>
              </div>
              <div className="card-body p-3 pb-0">
                <div className="row">
                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Validateur 1</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <select
                            {...register("validatorDecision1")}
                            className={
                              errors.validatorDecision1
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                          >
                            <option value="">Sélectionnez un validateur</option>
                            {allUsers.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.firstname} {user.lastname}
                              </option>
                            ))}
                          </select>

                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.validatorDecision1 && (
                            <div className="invalid-feedback">
                              {errors.validatorDecision1.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Validateur 2</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <select
                            {...register("validatorDecision2")}
                            className={
                              errors.validatorDecision2
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                          >
                            <option value="">Sélectionnez un validateur</option>
                            {allUsers.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.firstname} {user.lastname}
                              </option>
                            ))}
                          </select>

                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.validatorDecision2 && (
                            <div className="invalid-feedback">
                              {errors.validatorDecision2.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h5>Signataires Diplômes</h5>
              </div>
              <div className="card-body p-3 pb-0">
                <div className="row">
                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Nom Signataire 1</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("signatoryDiploma1")}
                            type="text"
                            className={
                              errors.signatoryDiploma1
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Dr Boubacar Sanogo"
                            aria-label="note_classe"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.signatoryDiploma1 && (
                            <div className="invalid-feedback">
                              {errors.signatoryDiploma1.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        Fonction Signataire 1
                      </label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("postSignatoryDiploma1")}
                            type="text"
                            className={
                              errors.postSignatoryDiploma1
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Recteur"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-briefcase" />
                          </span>
                          {errors.postSignatoryDiploma1 && (
                            <div className="invalid-feedback">
                              {errors.postSignatoryDiploma1.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Nom Signataire 2</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("signatoryDiploma2")}
                            type="text"
                            className={
                              errors.signatoryDiploma2
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Diallo Oumar"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.signatoryDiploma2 && (
                            <div className="invalid-feedback">
                              {errors.signatoryDiploma2.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        Fonction Signataire 2
                      </label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("postSignatoryDiploma2")}
                            type="text"
                            className={
                              errors.postSignatoryDiploma2
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Vice Recteur"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-briefcase" />
                          </span>
                          {errors.postSignatoryDiploma2 && (
                            <div className="invalid-feedback">
                              {errors.postSignatoryDiploma2.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label"> Nom Signataire 3</label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("signatoryDiploma3")}
                            type="text"
                            className={
                              errors.signatoryDiploma3
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Diallo Oumar"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-user-circle" />
                          </span>
                          {errors.signatoryDiploma3 && (
                            <div className="invalid-feedback">
                              {errors.signatoryDiploma3.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-6 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        Fonction Signataire 3
                      </label>
                      <div className="position-relative">
                        <div className="input-group">
                          <input
                            {...register("postSignatoryDiploma3")}
                            type="text"
                            className={
                              errors.postSignatoryDiploma3
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder="Ex: Vice Recteur"
                            aria-label="note_examen"
                            aria-describedby="basic-addon1"
                          />
                          <span className="input-group-text" id="basic-addon1">
                            <i className="ti ti-briefcase" />
                          </span>
                          {errors.postSignatoryDiploma3 && (
                            <div className="invalid-feedback">
                              {errors.postSignatoryDiploma3.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default DiplomeSettings;
